import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ImagePlus,
  Calendar as CalIcon,
  Clock,
  ListTree,
  PenLine,
  Send,
  Save,
  SlidersHorizontal,
  Loader2,
  Check,
  X,
} from 'lucide-react'
import { Modal } from '@/components/Modal'
import { channelProfileSource, ProfileImage } from '@/components/ChannelAvatar'
import { useStore } from '@/lib/store'
import { useUI } from '@/lib/ui'
import { PLATFORMS, type PlatformId } from '@/lib/platforms'
import {
  ComposerAccountToggle,
  ComposerAction,
  ComposerActionRail,
  ComposerAddAccount,
  ComposerFooterButton,
} from '@/components/beautiful-ui/ComposerPrimitives'
import { PostPreview } from './PostPreview'
import { reloadWorkspaceData, uploadMediaFile } from '@/lib/data'
import { trackEvent } from '@/lib/posthog'
import type { MediaItem, PostStatus } from '@/lib/types'
import clsx from 'clsx'

interface Prefill {
  content?: string
  channelIds?: string[]
  scheduledAt?: number
  mediaIds?: string[]
  overrides?: Record<string, string>
  editId?: string
}

// Networks where an over-limit post publishes as a reply chain instead of failing.
const THREADING_PLATFORMS: PlatformId[] = ['x', 'bluesky', 'mastodon', 'threads']

function toLocalDateTime(ts: number) {
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

export function Composer({
  open,
  onClose,
  prefill,
}: {
  open: boolean
  onClose: () => void
  prefill: Prefill | null
}) {
  const channels = useStore((s) => s.channels)
  const media = useStore((s) => s.media)
  const savePost = useStore((s) => s.savePost)
  const insertMedia = useStore((s) => s.insertMedia)
  const openConnect = useUI((s) => s.openConnect)

  const [content, setContent] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [mediaIds, setMediaIds] = useState<string[]>([])
  const [overrides, setOverrides] = useState<Record<string, string>>({})
  const [customizeId, setCustomizeId] = useState<string | null>(null)
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [mediaBusy, setMediaBusy] = useState(0)
  const [mediaError, setMediaError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState<PostStatus | null>(null)
  const [activePreview, setActivePreview] = useState(0)
  const [showMediaPicker, setShowMediaPicker] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const editId = prefill?.editId

  // Initialize whenever opened
  useEffect(() => {
    if (!open) return
    const init = prefill?.scheduledAt ?? defaultSlot()
    const { date: d, time: t } = toLocalDateTime(init)
    setContent(prefill?.content ?? '')
    setSelected(prefill?.channelIds?.length ? prefill.channelIds : channels.map((c) => c.id).slice(0, 1))
    setMediaIds(prefill?.mediaIds ?? [])
    setOverrides(prefill?.overrides ?? {})
    setCustomizeId(null)
    setDate(d)
    setTime(t)
    setActivePreview(0)
    setShowMediaPicker(false)
    setMediaError(null)
    setSaveError(null)
    setSaving(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const selectedChannels = useMemo(
    () => selected.map((id) => channels.find((c) => c.id === id)).filter(Boolean),
    [selected, channels],
  ) as NonNullable<ReturnType<typeof channels.find>>[]

  const selectedMedia = useMemo(
    () => mediaIds.map((id) => media.find((m) => m.id === id)).filter(Boolean) as MediaItem[],
    [mediaIds, media],
  )

  const platformsSelected = useMemo(
    () => Array.from(new Set(selectedChannels.map((c) => c.platform))) as PlatformId[],
    [selectedChannels],
  )

  const minLimit = useMemo(() => {
    if (platformsSelected.length === 0) return 5000
    return Math.min(...platformsSelected.map((p) => PLATFORMS[p].charLimit))
  }, [platformsSelected])

  const over = content.length > minLimit
  const scheduledAt = useMemo(() => {
    const ts = new Date(`${date}T${time}`).getTime()
    return Number.isNaN(ts) ? null : ts
  }, [date, time])
  const invalidSchedule = scheduledAt === null || scheduledAt <= Date.now()

  const toggleChannel = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  const onFiles = (files: FileList | null) => {
    if (!files) return
    setMediaError(null)
    Array.from(files).forEach(async (file) => {
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) return
      setMediaBusy((n) => n + 1)
      try {
        const item = await uploadMediaFile(file)
        insertMedia(item)
        setMediaIds((ids) => [...ids, item.id])
      } catch (e) {
        setMediaError(e instanceof Error ? e.message : 'Media upload failed')
      } finally {
        setMediaBusy((n) => n - 1)
      }
    })
  }

  const save = async (status: PostStatus) => {
    if (selected.length === 0 || (!content.trim() && selectedMedia.length === 0)) return
    const publishNow = status === 'published'
    if (status === 'scheduled' && invalidSchedule) {
      setSaveError('Choose a future date and time before scheduling.')
      return
    }
    const queuedStatus: PostStatus = publishNow ? 'scheduled' : status
    // Only keep overrides for channels the post targets, and drop empty ones.
    const cleanOverrides: Record<string, string> = {}
    for (const id of selected) {
      if (overrides[id]?.trim()) cleanOverrides[id] = overrides[id]
    }
    const payload = {
      content,
      channelIds: selected,
      mediaIds,
      overrides: Object.keys(cleanOverrides).length ? cleanOverrides : undefined,
      scheduledAt: publishNow ? Date.now() : scheduledAt ?? Date.now(),
      status: queuedStatus,
      publishNow,
    }

    setSaving(status)
    setSaveError(null)
    try {
      const post = await savePost(payload, editId)
      if (publishNow) await reloadWorkspaceData()

      const eventProps = {
        post_id: post.id,
        status: publishNow ? 'publish_requested' : status,
        channel_count: selected.length,
        platforms: platformsSelected,
        has_media: selectedMedia.length > 0,
        edited: Boolean(editId),
      }
      if (!editId) trackEvent('post_created', eventProps)
      if (status === 'scheduled') trackEvent('post_schedule_requested', eventProps)
      else if (publishNow) trackEvent('post_publish_requested', eventProps)

      onClose()
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Could not save this post. Try again.'
      const failureEvent = publishNow
        ? 'post_publish_request_failed'
        : status === 'scheduled'
          ? 'post_schedule_request_failed'
          : 'post_save_failed'
      trackEvent(failureEvent, {
        post_id: editId || null,
        status: publishNow ? 'publish_requested' : status,
        channel_count: selected.length,
        platforms: platformsSelected,
        has_media: selectedMedia.length > 0,
        edited: Boolean(editId),
        error_message: errorMessage,
      })
      setSaveError(errorMessage)
    } finally {
      setSaving(null)
    }
  }

  const canPost = selected.length > 0 && (content.trim().length > 0 || selectedMedia.length > 0)

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      className="!max-h-[92vh]"
      ariaLabel={editId ? 'Edit post' : 'Create a post'}
    >
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="grid size-8 place-items-center rounded-[10px] bg-inset text-ink-600 shadow-hairline">
            <PenLine size={15} strokeWidth={1.9} />
          </span>
          <div>
            <h2 className="font-display text-lg font-bold text-ink-900">
              {editId ? 'Edit post' : 'Create a post'}
            </h2>
            <p className="text-xs text-ink-400">
              Compose once, publish everywhere.
            </p>
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close composer" className="grid size-8 place-items-center rounded-[10px] text-ink-400 transition hover:bg-hover hover:text-ink-900">
          <X size={16} />
        </button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[1.15fr_0.85fr]">
        {/* LEFT: editor */}
        <div className="flex min-h-0 flex-col overflow-y-auto border-line p-4 lg:border-r">
          {/* channel selector */}
          <div className="mb-2.5">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-400">
                Publish to
              </span>
              {selected.length > 0 && (
                <span className="text-xs text-ink-400">{selected.length} selected</span>
              )}
            </div>
            {channels.length === 0 ? (
              <button
                type="button"
                onClick={openConnect}
                className="flex h-9 w-full items-center justify-center rounded-full bg-surface text-[12px] font-medium text-ink-500 shadow-hairline transition hover:bg-hover hover:text-ink-900"
              >
                Connect a channel to start
              </button>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {channels.map((c) => {
                  const on = selected.includes(c.id)
                  return (
                    <ComposerAccountToggle
                      key={c.id}
                      name={c.name}
                      icon={
                        <ProfileImage
                          src={channelProfileSource(c)}
                          name={c.name}
                          background={c.avatarColor || PLATFORMS[c.platform].gradient}
                          size={18}
                        />
                      }
                      selected={on}
                      onClick={() => toggleChannel(c.id)}
                    />
                  )
                })}
                <ComposerAddAccount onClick={openConnect} />
              </div>
            )}
          </div>

          {/* text editor */}
          <div className="relative">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What do you want to share?"
              className="min-h-[132px] w-full resize-none rounded-xl border border-line bg-surface p-3.5 text-[14px] leading-relaxed text-ink-900 placeholder:text-ink-400 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
            />
            <div
              className={clsx(
                'absolute bottom-3 right-3 rounded-full px-2 py-0.5 text-[11px] font-medium',
                over ? 'bg-accent-red/15 text-accent-red' : 'bg-panel text-ink-400',
              )}
            >
              {content.length}/{minLimit}
            </div>
          </div>

          {/* thread-split hints */}
          {platformsSelected
            .filter(
              (p) =>
                THREADING_PLATFORMS.includes(p) && content.length > PLATFORMS[p].charLimit,
            )
            .map((p) => {
              const limit = PLATFORMS[p].charLimit
              const parts = Math.ceil(content.length / Math.max(20, limit - 8))
              return (
                <div
                  key={p}
                  className="mt-2 flex items-center gap-2 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-xs text-brand-600"
                >
                  <ListTree size={14} />
                  Longer than {PLATFORMS[p].name}&apos;s {limit} characters: publishes there as a{' '}
                  {parts}-part thread.
                </div>
              )
            })}

          {/* per-channel overrides */}
          {selectedChannels.length > 0 && (
            <div className="mt-2.5 rounded-xl border border-line bg-panel p-2.5">
              <div className="mb-1.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                <SlidersHorizontal size={14} /> Customize per channel
                <span className="font-normal normal-case tracking-normal text-ink-400">
                  optional, defaults to the main text
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {selectedChannels.map((c) => {
                  const active = customizeId === c.id
                  const hasOverride = Boolean(overrides[c.id]?.trim())
                  return (
                    <button
                      key={c.id}
                      onClick={() => setCustomizeId(active ? null : c.id)}
                      className={clsx(
                        'flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11.5px] font-medium transition',
                        active
                          ? 'bg-surface text-ink-900 shadow-card'
                          : 'text-ink-500 hover:bg-surface hover:text-ink-900',
                      )}
                    >
                          <ProfileImage
                            src={channelProfileSource(c)}
                            name={c.name}
                            background={c.avatarColor || PLATFORMS[c.platform].gradient}
                            size={14}
                          />
                      {c.name}
                      {hasOverride && <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />}
                    </button>
                  )
                })}
              </div>
              {customizeId &&
                (() => {
                  const ch = selectedChannels.find((c) => c.id === customizeId)
                  if (!ch) return null
                  const limit = PLATFORMS[ch.platform].charLimit
                  const value = overrides[ch.id] ?? ''
                  const effective = value.trim() ? value : content
                  const overLimit = effective.length > limit
                  const threads = THREADING_PLATFORMS.includes(ch.platform)
                  return (
                    <div className="relative mt-2">
                      <textarea
                        value={value}
                        onChange={(e) =>
                          setOverrides((o) => ({ ...o, [ch.id]: e.target.value }))
                        }
                        placeholder={`Custom text for ${ch.name} (leave empty to use the main text)`}
                        className="min-h-[90px] w-full resize-none rounded-xl border border-line bg-surface p-3 text-sm leading-relaxed text-ink-900 placeholder:text-ink-400 outline-none transition focus:border-brand-500"
                      />
                      <div className="mt-1 flex items-center justify-between text-[11px]">
                        <span className={overLimit && !threads ? 'text-accent-red' : 'text-ink-400'}>
                          {overLimit
                            ? threads
                              ? `Over ${limit}: will publish as a thread`
                              : `Over ${PLATFORMS[ch.platform].name}'s ${limit} character limit`
                            : `${effective.length}/${limit}`}
                        </span>
                        {value.trim() && (
                          <button
                            onClick={() =>
                              setOverrides((o) => {
                                const { [ch.id]: _drop, ...rest } = o
                                return rest
                              })
                            }
                            className="text-ink-400 transition hover:text-accent-red"
                          >
                            Reset to main text
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })()}
            </div>
          )}

          {/* selected media chips */}
          {selectedMedia.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {selectedMedia.map((m) => (
                <div
                  key={m.id}
                  className="group relative h-16 w-16 overflow-hidden rounded-xl border border-line"
                >
                  {m.url ? (
                    <img src={m.url} className="h-full w-full object-cover" alt="" />
                  ) : (
                    <div className="grid h-full w-full place-items-center bg-ink-100 text-[10px] font-medium text-ink-500">
                      Unavailable
                    </div>
                  )}
                  <button
                    onClick={() => setMediaIds((ids) => ids.filter((id) => id !== m.id))}
                    className="absolute right-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-full bg-black/70 text-white opacity-0 transition group-hover:opacity-100"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* media toolbar */}
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <ComposerActionRail>
            <ComposerAction
              onClick={() => fileRef.current?.click()}
              disabled={mediaBusy > 0}
              active
              icon={mediaBusy > 0 ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            >
              {mediaBusy > 0 ? 'Uploading...' : 'Media'}
            </ComposerAction>
            {media.length > 0 && (
              <ComposerAction
                onClick={() => setShowMediaPicker((v) => !v)}
                active={showMediaPicker}
              >
                Library
              </ComposerAction>
            )}
            </ComposerActionRail>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              onChange={(e) => {
                onFiles(e.target.files)
                e.target.value = ''
              }}
            />
          </div>
          {mediaError && (
            <p className="mt-2 rounded-xl border border-accent-red/30 bg-accent-red/10 px-3 py-2 text-xs text-accent-red">
              {mediaError}
            </p>
          )}

          {/* library picker */}
          {showMediaPicker && (
            <div className="mt-3 grid grid-cols-5 gap-2 rounded-xl border border-line bg-panel p-2">
              {media.map((m) => {
                const on = mediaIds.includes(m.id)
                return (
                  <button
                    key={m.id}
                    onClick={() =>
                      setMediaIds((ids) =>
                        ids.includes(m.id) ? ids.filter((x) => x !== m.id) : [...ids, m.id],
                      )
                    }
                    className={clsx(
                      'relative aspect-square overflow-hidden rounded-lg border-2',
                      on ? 'border-brand-500' : 'border-transparent',
                    )}
                  >
                    {m.url ? (
                      <img src={m.url} className="h-full w-full object-cover" alt="" />
                    ) : (
                      <div className="grid h-full w-full place-items-center bg-ink-100 text-[10px] font-medium text-ink-500">
                        Unavailable
                      </div>
                    )}
                    {on && (
                      <span className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-brand-500">
                        <Check size={10} className="text-white" />
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          {/* schedule */}
          <div className="mt-2.5 rounded-xl border border-line bg-panel p-2.5">
            <div className="mb-1.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              <CalIcon size={14} /> Schedule
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex h-8 items-center gap-2 rounded-full bg-surface px-2.5 shadow-hairline">
                <CalIcon size={15} className="text-ink-400" />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="bg-transparent text-sm text-ink-900 outline-none [color-scheme:light]"
                />
              </div>
              <div className="flex h-8 items-center gap-2 rounded-full bg-surface px-2.5 shadow-hairline">
                <Clock size={15} className="text-ink-400" />
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="bg-transparent text-sm text-ink-900 outline-none [color-scheme:light]"
                />
              </div>
              {invalidSchedule && (
                <span className="text-xs text-accent-amber">Choose a future time</span>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: previews */}
        <div className="flex min-h-0 flex-col overflow-y-auto bg-panel p-4">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            Preview
          </div>
          {platformsSelected.length === 0 ? (
            <div className="grid flex-1 place-items-center text-center text-sm text-ink-400">
              Select a channel to preview your post.
            </div>
          ) : (
            <>
              {platformsSelected.length > 1 && (
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {platformsSelected.map((p, i) => (
                    <button
                      key={p}
                      onClick={() => setActivePreview(i)}
                      className={clsx(
                        'flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11.5px] font-medium transition',
                        activePreview === i
                          ? 'bg-surface text-ink-900 shadow-card'
                          : 'text-ink-500 hover:bg-surface',
                      )}
                    >
                      <span className="h-3.5 w-3.5">{PLATFORMS[p].icon}</span>
                      {PLATFORMS[p].name}
                    </button>
                  ))}
                </div>
              )}
              {(() => {
                const platform =
                  platformsSelected[Math.min(activePreview, platformsSelected.length - 1)]
                const channel = selectedChannels.find((c) => c.platform === platform)
                if (!channel) return null
                const override = overrides[channel.id]
                return (
                  <PostPreview
                    platform={platform}
                    channel={channel}
                    content={override?.trim() ? override : content}
                    media={selectedMedia}
                  />
                )
              })()}
            </>
          )}
        </div>
      </div>

      {/* footer actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
        <div>
          <ComposerFooterButton
            onClick={() => void save('draft')}
            disabled={!canPost || saving !== null}
            variant="quiet"
            icon={saving === 'draft' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          >
            {saving === 'draft' ? 'Saving...' : 'Save draft'}
          </ComposerFooterButton>
          {saveError && <p className="mt-1 max-w-sm text-xs text-accent-red">{saveError}</p>}
        </div>
        <div className="flex items-center gap-2">
          <ComposerFooterButton
            onClick={() => void save('published')}
            disabled={!canPost || saving !== null}
            variant="secondary"
            icon={saving === 'published' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          >
            {saving === 'published' ? 'Posting...' : 'Post now'}
          </ComposerFooterButton>
          <ComposerFooterButton
            onClick={() => void save('scheduled')}
            disabled={!canPost || invalidSchedule || saving !== null}
            variant="primary"
            icon={saving === 'scheduled' ? <Loader2 size={14} className="animate-spin" /> : <CalIcon size={14} />}
          >
            {saving === 'scheduled' ? 'Scheduling...' : editId ? 'Update schedule' : 'Schedule'}
          </ComposerFooterButton>
        </div>
      </div>
    </Modal>
  )
}

function defaultSlot() {
  const d = new Date()
  d.setHours(d.getHours() + 1, 0, 0, 0)
  return d.getTime()
}
